<script>
    // Licensed under Gnu Public Licence version 2
    // Copyright (c) 2020-2023 (original work) Open Assessment Technologies SA ;
    import { __, getLanguageDirection } from '@oat-sa-private/ui-core';
    import Icon from '../icon/Icon.svelte';

    /**
     * @typedef {Object} ValidityMessage
     * @property {Boolean} valid
     * @property {String} msg
     * @property {String} [type]
     */

    /**
     * @typedef {Object} Validity
     * @property {ValidityMessage[]} messages
     * @property {Boolean} overallValidity
     */

    /**
     * Component is used to make some content draggable
     * @property {Validity} validity - validity state object
     * @property {boolean|Object.<string, boolean>} [showValidFeedback=true] - Enables/disables the valid feedback messages. If `Object`, does so for each feedback message individually. Uses `type` property of message object as key.
     * @property {boolean|Object.<string, boolean>} [showInvalidFeedback=true] - Enables/disables the invalid feedback messages. If `Object`, does so for each feedback message individually. Uses `type` property of message object as key.
     * @property {boolean} emptyInput - Lets the feedback know that the accompanying input field is empty, so errors can be hidden
     * @property {boolean} [firstTry=true] - Lets the feedback know that the user is still on their first try, so feedback can be more "indulgent" (shows a neutral non-error state when the input is invalid)
     * @property {number} [feedbackAnnounceTimeout=500] - Feedback will be announced only after defined timeout to avoid repeating during typing
     * @property {string} [lang] - language code - lang attribute value for container element
     */

    export let validity = { messages: [], overallValidity: true };
    export let showValidFeedback = true;
    export let showInvalidFeedback = true;
    export let emptyInput;
    export let firstTry = true; // behaviour differs after first blur
    export let feedbackAnnounceTimeout = 500;
    export let lang;

    // cast prop data:
    if (!Array.isArray(validity.messages)) {
        validity.messages = [];
    }
    validity.overallValidity = Boolean(validity.overallValidity);

    $: validity && setFeedbackAnnounce();
    $: dir = lang ? getLanguageDirection(lang) : void 0;

    let feedbackAnnounceTimer;
    let feedbackAnnounce = false;

    /**
     * Debounces SR announce with timer
     */
    function setFeedbackAnnounce() {
        clearTimeout(feedbackAnnounceTimer);
        feedbackAnnounce = false;
        feedbackAnnounceTimer = setTimeout(() => {
            feedbackAnnounce = true;
        }, feedbackAnnounceTimeout);
    }

    /**
     * Check if specific valid feedback should be hidden
     * @param {ValidityMessage} line
     * @returns {Boolean}
     */
    function isValidLineHidden(line) {
        return (
            typeof showValidFeedback === 'object' &&
            line.type &&
            line.type in showValidFeedback &&
            !showValidFeedback[line.type]
        );
    }

    /**
     * Check if specific invalid feedback should be hidden
     * @param {ValidityMessage} line
     * @returns {Boolean}
     */
    function isInvalidLineHidden(line) {
        return (
            typeof showInvalidFeedback === 'object' &&
            line.type &&
            line.type in showInvalidFeedback &&
            !showInvalidFeedback[line.type]
        );
    }

    /**
     * Check if specific feedback should be hidden
     * @param {ValidityMessage} line
     * @returns {Boolean}
     */
    function isLineHidden(line) {
        return (line.valid && isValidLineHidden(line)) || (!line.valid && isInvalidLineHidden(line));
    }
</script>

<style>
    /* Feedback styles */
    .feedback-inline {
        position: absolute;
        inset-inline-start: 0;
        inset-inline-end: auto;
        inset-block-end: calc(100% - var(--border-thick));
        min-inline-size: 100%;
        z-index: var(--layer-1);

        & > * {
            white-space: nowrap;
            color: var(--color-text-inverted);
            font-size: var(--fontsize-body-s);
            line-height: 2;
            padding-inline: var(--space-1x);
            padding-block: 0;
            margin: 0;
            display: grid;
            grid-template-columns: 1fr var(--space-2x);
            align-items: center;
            z-index: var(--layer-1);

            &.valid {
                background-color: var(--color-bg-feedback);
            }
            &.invalid {
                background-color: var(--color-bg-warning);
            }
            /* check icon */
            & :global(svg) {
                margin-inline: var(--space-half);
                margin-block: 0;
            }
        }
    }

    .feedback-inline.hide-invalid span.invalid,
    .feedback-inline.hide-valid span.valid,
    .feedback-inline span.hide-invalid.invalid,
    .feedback-inline span.hide-valid.valid {
        display: none;
    }
</style>

{#if showValidFeedback || showInvalidFeedback}
    <span
        class="feedback-inline"
        class:hide-valid={!showValidFeedback}
        class:hide-invalid={!showInvalidFeedback}
        aria-live="assertive"
        aria-relevant="all"
        {lang}
        {dir}>
        {#if validity.messages}
            {#each validity.messages as line}
                <span
                    aria-hidden="true"
                    class={line.valid || firstTry ? 'valid' : 'invalid'}
                    class:hide-valid={isValidLineHidden(line, showValidFeedback)}
                    class:hide-invalid={isInvalidLineHidden(line, showInvalidFeedback)}>
                    <span> {line.msg} </span>
                    {#if !emptyInput && line.valid}
                        <Icon name="check-12" />
                    {/if}
                </span>
                {#if feedbackAnnounce && !isLineHidden(line, showInvalidFeedback, showValidFeedback)}
                    <span class="visually-hidden"> {line.valid ? __('valid:') : __('invalid:')} {line.msg} </span>
                {/if}
            {/each}
        {/if}
    </span>
{/if}
